import * as Luxon from 'luxon'
import ProfileImageWithFallback from 'components/ProfileImageWithFallback'
import { getTweetTextWithUrls, getTweetUrl, getTwitterUserUrl } from 'utilities/twitter'
import { TweetProcessed } from 'data/twitter/types'
import styles from './Message.module.css'
import TwitterUserLink from 'components/TwitterUserLink'
import { isNil, partition } from 'lodash'
import { getFormattedDate } from 'utilities/date'
import Switch from 'components/Switch'
import { useContext, useState } from 'react'
import { BlotterContext } from 'components/EmbeddedWidget'
import { updateTweetConversationIsEnabled } from 'data/comments/client'
// import { Conversation } from 'data/conversations/types'
import { updateConversations } from 'data/conversations/client'
import { EyeIcon, EyeOffIcon } from '@heroicons/react/outline'
import Tooltip from 'components/Tooltip'
// import { HeartIcon } from '@heroicons/react/outline'
// import TwitterIcon from 'components/Icons/Twitter'

type MessageProps = {
  message: TweetProcessed,
  isAuthor?: (authorId: string) => boolean,
  parent?: TweetProcessed,
  replies?: TweetProcessed[],
  isPreview?: boolean,
  // conversation?: Conversation, // this should be switched to just conversationId, we don't need the whole conversation
  conversationId?: string,
}

const Message = ({
  message,
  isAuthor,
  parent,
  replies,
  isPreview = false,
  conversationId,
}: MessageProps) => {
  // console.log('MESSAGE', message.text, replies)
  // const isAdmin = useIsAdminUserForBlog()
  const { isAdmin } = useContext(BlotterContext)
  const [isSwitching, setIsSwitching] = useState(false)
  // console.log('IN MESSAGE', { message, isAdmin, hostId })
  const author_username = message.author_username as string
  const created_at = message.external_created_at as string
  const external_id = message.external_id as string
  // const likeCount = message.public_metrics?.like_count
  // const author_id = message.author_id as string
  // console.log('MESSAGE', { message })
  const isEnabled =
    isNil(message.tweetConversations?.[0]?.isEnabled)
      ? true
      : message.tweetConversations?.[0]?.isEnabled
  // console.log({ text: message.text, messageEnabled: message?.tweetConversations?.[0]?.isEnabled, isEnabled })
  // console.log({ media: message.media })
  const [selfReplies, otherReplies] = partition(replies, (reply) => reply.author_id === message.author_id)
  // console.log({ selfReplies, otherReplies })
  return <div className={styles.messageContainer}>
    <div className={
      isEnabled || isAdmin
        ? styles.messageWrapper
        : styles.messageWrapperHidden
    }>
      <div className={
        isEnabled
        ? styles.message
        : styles.messageHidden
      }>
        <div className={styles.messageAuthorImageContainer}>
          <ProfileImageWithFallback
            className={styles.messageAuthorImage}
            src={message.profile_image_url}
            alt={`Pic of ${message.author_name}`}
          />
          {/* <div className='relative mx-auto mt-1 flex justify-center'>
            <HeartIcon className='w-5 h-5 text-slate-400' />
            { !!likeCount && <div className='absolute right-[86%] top-0 bottom-0 flex items-center text-xs text-slate-400'>{likeCount}</div> }
          </div> */}
        </div>
        <div className={styles.messageContent}>
          <div className={styles.messageHeading}>
            <div className={styles.messageAuthor}>
              <div className={styles.messageName}>
                <TwitterUserLink username={author_username}>
                  { message.author_name }
                </TwitterUserLink>
              </div>
              <div className={styles.messageUserName}>
                <TwitterUserLink username={author_username}>
                  @{ author_username }
                </TwitterUserLink>
              </div>
              <div className={styles.tweetDate}>
                <TwitterTweetLink
                  conversationId={ external_id }
                  username={ author_username }
                  isPreview={ isPreview }
                >
                  { !!created_at && <TwitterDate date={created_at} /> }
                </TwitterTweetLink>
              </div>
                {/* TODO: Fix isAuthor work — if someone else tweets your URL, they are not an author */}
                {/*       But "Conversation Starter" is a weird term no one will understand */}
              {/* { isAuthor?.(author_id) &&
                <div
                  className={styles.authorLabel}
                  title='This comment started a new conversation'
                >
                  <TwitterUserLink username={author_username}>
                    Conversation Starter
                  </TwitterUserLink>
                </div>
              } */}
            </div>
          </div>
          <blockquote>{ getTweetTextWithUrls(message, styles.link, parent) }</blockquote>
          {/* { !message.media && <a role='button' className={styles.messageLinkInline} href={getTweetUrl(author_username, message.external_id as string)} target='_out'>Message</a>} */}
          { message.media && <>
            { message.media?.filter(mediaItem => mediaItem).map(mediaItem =>
              <div className={styles.media} key={mediaItem.media_key}>
                { ['animated_gif', 'image', 'photo'].includes(mediaItem?.type) &&
                  <img src={mediaItem.url || mediaItem.preview_image_url} style={{ height: 'auto', width: mediaItem.width }} />
                }
                { ['video'].includes(mediaItem?.type) &&
                  <video loop autoPlay height={mediaItem.height} width={mediaItem.width}>
                    <source src={mediaItem.preview_image_url} type='video/mp4' />
                  </video>
                }
              </div>
            )}
            {/* <div className={styles.threadMessage}>
              <a role='button' className={styles.messageLink} href={getTweetUrl(author_username, external_id)} target='_out'>Message</a>
            </div> */}
          </>}
          { !!isAdmin && <div className={styles.isVisibleToggle}>
            <Tooltip
              className='w-40'
              color={ isEnabled ? 'bg-emerald-600' : 'bg-red-600' }
              tooltip={<>This is { isEnabled ? 'visible' : 'hidden' }</>}
            >
              <Switch
                className={isEnabled ? styles.switch : styles.switchDisabled}
                enabled={!!isEnabled}
                setEnabled={async () => {
                  if (conversationId) {
                    setIsSwitching(true)
                    await updateTweetConversationIsEnabled(conversationId, external_id, !isEnabled)
                    updateConversations()
                    setIsSwitching(false)
                  }
                }}
                isSwitching={isSwitching}
                onIcon={<EyeIcon className={styles.onIcon} />}
                offIcon={<EyeOffIcon className={styles.offIcon} />}
              />
            </Tooltip>
          </div> }
        </div>
      </div>
    </div>
    <div className={styles.threadContainer}>
      { selfReplies
        ?.filter(subMessage => subMessage.reply_to_id === message.external_id)
        .map(subMessage =>
          <Message
            key={`selfReply:${subMessage.id}`}
            message={subMessage}
            isAuthor={isAuthor}
            parent={message}
            replies={replies}
            isPreview={isPreview}
            conversationId={conversationId}
          />
        )
      }
    </div>
    <div className={styles.repliesContainer}>
      { otherReplies
        ?.filter(subMessage => subMessage.reply_to_id === message.external_id)
        .map(subMessage =>
          <Message
            key={`otherReply:${subMessage.id}`}
            message={subMessage}
            isAuthor={isAuthor}
            parent={message}
            replies={replies}
            isPreview={isPreview}
            conversationId={conversationId}
          />
        )
      }
    </div>
  </div>
}

type TweetLinkProps = {
  username: string,
  conversationId: string,
  children: React.ReactNode,
  isPreview?: boolean,
}

export const TwitterTweetLink = ({
  username,
  conversationId,
  children,
  isPreview = false,
}: TweetLinkProps) =>
  <a role='button' className={styles.link} href={ isPreview ? getTwitterUserUrl(username) : getTweetUrl(username, conversationId)} target='_out'>{ children }</a>

type TwitterDateProps = {
  date: string,
}

const TwitterDate = ({
  date,
}: TwitterDateProps) =>
  <div
    className={styles.messageDate}
    title={Luxon.DateTime.fromISO(date)?.toLocaleString(Luxon.DateTime.DATETIME_MED_WITH_SECONDS)}
  >
    {/* { Luxon.DateTime.fromISO(date)?.toRelative() } */}
    { getFormattedDate(date)}
  </div>

// const formatPostText = (tweet: TweetProcessed) => {
//   // console.log({ tweet })
//   const tweetTextWithUrls = getTweetTextWithUrls(tweet, styles.link)
//   // const cleanedTweetText = cleanTweetContent(tweetTextWithUrls)
//   // console.log({ tweetTextWithUrls })
//   return tweetTextWithUrls
// }

export default Message
